/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import PropTypes from "prop-types";
import { OuiI18n } from '../i18n';
import { OuiDraggable } from '../drag_and_drop';
import { OuiScreenReaderOnly } from '../accessibility';
import { OuiFlexGroup, OuiFlexItem } from '../flex';
import { OuiButtonIcon, OuiButtonGroup } from '../button';
import { OuiIcon } from '../icon';
import { OuiText } from '../text';
import { getDetailsForSchema } from './data_grid_schema';
import { OuiToken } from '../token';
export var defaultSortAscLabel = /*#__PURE__*/React.createElement(OuiI18n, {
  token: "ouiColumnSortingDraggable.defaultSortAsc",
  default: "A-Z"
});
export var defaultSortDescLabel = /*#__PURE__*/React.createElement(OuiI18n, {
  token: "ouiColumnSortingDraggable.defaultSortDesc",
  default: "Z-A"
});
export var OuiDataGridColumnSortingDraggable = ({
  id,
  display,
  direction,
  index,
  sorting,
  schema,
  schemaDetectors,
  ...rest
}) => {
  const schemaDetails = schema.hasOwnProperty(id) && schema[id].columnType != null ? getDetailsForSchema(schemaDetectors, schema[id].columnType) : null;
  const textSortAsc = schemaDetails != null ? schemaDetails.sortTextAsc : defaultSortAscLabel;
  const textSortDesc = schemaDetails != null ? schemaDetails.sortTextDesc : defaultSortDescLabel;
  const toggleOptions = [{
    id: `${id}Asc`,
    value: 'asc',
    label: textSortAsc,
    'data-test-subj': `ouiDataGridColumnSorting-sortColumn-${id}-asc`
  }, {
    id: `${id}Desc`,
    value: 'desc',
    label: textSortDesc,
    'data-test-subj': `ouiDataGridColumnSorting-sortColumn-${id}-desc`
  }];
  return <OuiDraggable draggableId={id} index={index} {...rest}>
      {(provided, state) => <div className={`ouiDataGridColumnSorting__item ${state.isDragging && 'ouiDataGridColumnSorting__item-isDragging'}`}>
          <OuiScreenReaderOnly>
            <p>
              <OuiI18n token="ouiColumnSortingDraggable.activeSortLabel" default="is sorting this data grid">
                {activeSortLabel => <span>
                    {display} {activeSortLabel}
                  </span>}
              </OuiI18n>
            </p>
          </OuiScreenReaderOnly>
          <OuiFlexGroup gutterSize="xs" alignItems="center" responsive={false} data-test-subj={`ouiDataGridColumnSorting-sortColumn-${id}`}>
            <OuiFlexItem grow={false}>
              <OuiI18n token="ouiColumnSortingDraggable.removeSortLabel" default="Remove from data grid sort:">
                {removeSortLabel => <OuiButtonIcon color="text" className="ouiDataGridColumnSorting__button" aria-label={`${removeSortLabel} ${id}`} iconType="cross" onClick={() => {
              const nextColumns = [...sorting.columns];
              const columnIndex = nextColumns.map(({
                id
              }) => id).indexOf(id);
              nextColumns.splice(columnIndex, 1);
              sorting.onSort(nextColumns);
            }} />}
              </OuiI18n>
            </OuiFlexItem>

            <OuiFlexItem grow={false}>
              <OuiToken color={schemaDetails != null ? schemaDetails.color : undefined} iconType={schemaDetails != null ? schemaDetails.icon : 'tokenString'} />
            </OuiFlexItem>
            <OuiFlexItem aria-hidden>
              <OuiText size="xs">
                <p>{display}</p>
              </OuiText>
            </OuiFlexItem>
            <OuiFlexItem className="ouiDataGridColumnSorting__orderButtons">
              <OuiI18n token="ouiColumnSortingDraggable.toggleLegend" default="Select sorting method for field: ">
                {toggleLegend => <OuiButtonGroup legend={`${toggleLegend} ${id}`} name={id} isFullWidth options={toggleOptions} data-test-subj={`-${direction}`} buttonSize="compressed" className="ouiDataGridColumnSorting__order" idSelected={direction === 'asc' ? `${id}Asc` : `${id}Desc`} onChange={(_, direction) => {
              const nextColumns = [...sorting.columns];
              const columnIndex = nextColumns.map(({
                id
              }) => id).indexOf(id);
              nextColumns.splice(columnIndex, 1, {
                id,
                direction
              });
              sorting.onSort(nextColumns);
            }} />}
              </OuiI18n>
            </OuiFlexItem>
            <OuiFlexItem grow={false} {...provided.dragHandleProps}>
              <div {...provided.dragHandleProps}>
                <OuiIcon type="grab" color="subdued" />
              </div>
            </OuiFlexItem>
          </OuiFlexGroup>
        </div>}
    </OuiDraggable>;
};
OuiDataGridColumnSortingDraggable.propTypes = {
  id: PropTypes.string.isRequired,
  direction: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  sorting: PropTypes.shape({
    /**
       * A function that receives updated column sort details in response to user interactions in the toolbar controls
       */
    onSort: PropTypes.func.isRequired,
    /**
       * An array of the column ids currently being sorted and their sort direction. The array order determines the sort order. `{ id: 'A'; direction: 'asc' }`
       */
    columns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      direction: PropTypes.oneOf(["asc", "desc"]).isRequired
    }).isRequired).isRequired
  }).isRequired,
  schema: PropTypes.shape({}).isRequired,
  schemaDetectors: PropTypes.arrayOf(PropTypes.shape({
    /**
       * The name of this data type, matches #OuiDataGridColumn / schema `schema`
       */
    type: PropTypes.string.isRequired,
    /**
       * The function given the text value of a cell and returns a score of [0...1] of how well the value matches this data type
       */
    detector: PropTypes.func.isRequired,
    /**
       * A custom comparator function when performing in-memory sorting on this data type, takes `(a: string, b: string, direction: 'asc' | 'desc) => -1 | 0 | 1`
       */
    comparator: PropTypes.func,
    /**
       * The icon used to visually represent this data type. Accepts any `OuiIcon IconType`.
       */
    icon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
    /**
       * The color associated with this data type; it's used to color the icon token
       */
    color: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["ouiColorVis0", "ouiColorVis1", "ouiColorVis2", "ouiColorVis3", "ouiColorVis4", "ouiColorVis5", "ouiColorVis6", "ouiColorVis7", "ouiColorVis8", "ouiColorVis9", "gray"]).isRequired, PropTypes.string.isRequired]).isRequired, PropTypes.string.isRequired]),
    /**
       * Text for how to represent an ascending sort of this data type, e.g. 'A -> Z'
       */
    sortTextAsc: PropTypes.node.isRequired,
    /**
       * Text for how to represent a descending sort of this data type, e.g. 'Z -> A'
       */
    sortTextDesc: PropTypes.node.isRequired,
    /**
       * Whether this column is sortable (defaults to true)
       */
    isSortable: PropTypes.bool,
    /**
       *  This property controls the capitalization of text
       */
    textTransform: PropTypes.oneOf(["uppercase", "lowercase", "capitalize"]),
    /**
       * Default sort direction of the column
       */
    defaultSortDirection: PropTypes.oneOf(["asc", "desc"])
  }).isRequired).isRequired,
  /**
     * Value to be shown in column sorting popover.
     */
  display: PropTypes.string.isRequired
};
try {
  OuiDataGridColumnSortingDraggable.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/datagrid/column_sorting_draggable.tsx',
    description: '',
    displayName: 'OuiDataGridColumnSortingDraggable',
    methods: [],
    props: {
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        },
        declarations: [{
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      direction: {
        defaultValue: null,
        description: '',
        name: 'direction',
        parent: {
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        },
        declarations: [{
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      index: {
        defaultValue: null,
        description: '',
        name: 'index',
        parent: {
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        },
        declarations: [{
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      sorting: {
        defaultValue: null,
        description: '',
        name: 'sorting',
        parent: {
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        },
        declarations: [{
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        }],
        required: true,
        type: {
          name: 'OuiDataGridSorting'
        }
      },
      schema: {
        defaultValue: null,
        description: '',
        name: 'schema',
        parent: {
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        },
        declarations: [{
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        }],
        required: true,
        type: {
          name: 'OuiDataGridSchema'
        }
      },
      schemaDetectors: {
        defaultValue: null,
        description: '',
        name: 'schemaDetectors',
        parent: {
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        },
        declarations: [{
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        }],
        required: true,
        type: {
          name: 'OuiDataGridSchemaDetector[]'
        }
      },
      display: {
        defaultValue: null,
        description: 'Value to be shown in column sorting popover.',
        name: 'display',
        parent: {
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        },
        declarations: [{
          fileName: 'src/src/components/datagrid/column_sorting_draggable.tsx',
          name: 'OuiDataGridColumnSortingDraggableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['OuiDataGridColumnSortingDraggableProps']
  };
} catch (e) {}